import { purchaseOrderService } from '#client/services/purchaseOrderServices';
import { inventoryTransferService } from '#client/services/inventoryTransferServices';
import { Button } from '@shopify/polaris';
import { ExportIcon } from '@shopify/polaris-icons';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { useState } from 'react';

const EXPORT_TYPES = {
    TransferException: {
        fetch: (filters) => inventoryTransferService.exportTransferExceptions(filters),
        fileName: 'TransferExceptions',
        emptyMessage: 'No transfer exceptions found',
        dateColumns: ['Date Created', 'Date Shipped', 'Last Updated'],
    },
    PurchaseOrder: {
        fetch: (filters) => purchaseOrderService.exportPurchaseOrders(filters),
        fileName: 'PurchaseOrders',
        emptyMessage: 'No purchase orders found',
        dateColumns: ['startDate', 'expectedShipDate', 'cancelDate', 'closeDate'],
    },
};

const ExportButton = ({ type, filters }) => {
    const { addToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    async function handleExport() {
        const config = EXPORT_TYPES[type];
        if (isLoading || !config) return;
        setIsLoading(true);
        try {
            const response = await config.fetch(filters);
            const { data, truncated } = response.data.data;
            if (data.length === 0) {
                addToast({ message: config.emptyMessage, type: 'error' });
                return;
            }
            downloadFile(config.fileName, convertDatesToUserTimezone(data, config.dateColumns));
            if (truncated) {
                addToast({ message: `Export limited to ${data.length.toLocaleString()} short lines. More results matched; narrow your filters to export the rest.`, type: 'warning', timeout: 10000 });
            }
        } catch {
            addToast({ message: 'Unable to export. Please try again.', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Button icon={ExportIcon} onClick={handleExport} loading={isLoading} disabled={isLoading}>
            Export
        </Button>
    );
};

function downloadFile(fileName, data) {
    const csvData = new Blob([json2CSV(data)], { type: 'text/csv;charset=utf-8;' });
    const csvURL = URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        URL.revokeObjectURL(csvURL);
    }
}

const csvCell = (value) => {
    if (value == null) return '';
    if (typeof value !== 'string') return String(value);
    const safeValue = /^\s*[=+\-@]|^[\t\r\n]/.test(value) ? `'${value}` : value;
    return `"${safeValue.replace(/"/g, '""')}"`;
};

function json2CSV(jsonData) {
    const data = Array.isArray(jsonData) ? jsonData : [jsonData];
    if (!data.length) return '';
    const keys = [...new Set(data.flatMap((row) => Object.keys(row)))];
    return [keys.map(csvCell).join(','), ...data.map((row) => keys.map((key) => csvCell(row[key])).join(','))].join('\n');
}

function convertDatesToUserTimezone(data, dateColumns) {
    const dateKeys = new Set(dateColumns);
    return data.map((row) =>
        Object.fromEntries(
            Object.entries(row).map(([key, value]) => {
                if (!dateKeys.has(key) || typeof value !== 'string' || !value) return [key, value];
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return [key, value];
                const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
                return [key, adjusted.toISOString()];
            })
        )
    );
}

export default ExportButton;
